import { Stack, Text } from "@certn/cascadia";
import { Checkbox, Icon } from "@certn/cascadia-react";
import { getInnerContentTextStyles } from "@certn/cascadia-react/components/summary-card/inner-content/utils";
import { slugify } from "@certn/utils";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

function CheckboxSummaryControl(props: CascadiaControlProps) {
  const { options } = props;
  const labelAlign = props.uischema.options?.labelAlign;
  const { t } = useTranslation();

  const summaryLabel = useMemo(() => {
    if (props.data) {
      return props.options?.summaryTrueLabel ?? t("Yes");
    }
    return props.options?.summaryFalseLabel ?? t("No");
  }, [props.data, props.options]);
  const summaryIcon = useMemo(() => {
    if (props.data && props.options?.summaryTrueIcon) {
      return (
        <Icon
          name={props.options.summaryTrueIcon}
          color={props.options?.summaryTrueIconColor}
        />
      );
    }
    if (!props.data && props.options?.summaryFalseIcon) {
      return (
        <Icon
          name={props.options.summaryFalseIcon}
          color={props.options?.summaryFalseIconColor}
        />
      );
    }
    return <></>;
  }, [props.data, props.options]);
  const textStyles = getInnerContentTextStyles(labelAlign);

  if (options?.include_summary === false) {
    return null;
  }

  return (
    <SummaryItem
      label={props.options?.hideSummaryLabel ? "" : props.label}
      labelAlign={labelAlign}
    >
      <Stack orientation="horizontal" gap="space-2">
        {summaryIcon}
        <Text style={textStyles.value.style} color={textStyles.value.color}>
          {summaryLabel}
        </Text>
      </Stack>
    </SummaryItem>
  );
}

export function CheckboxControl(props: CascadiaControlProps) {
  const { options, description, errors, path } = props;
  const { isSummaryForPath } = useContext(CascadiaJsonFormContext);

  if (
    isSummaryForPath?.(path) &&
    // some fields are displayed as readonly text boxes rather than summary items
    !props.options?.renderSummaryAsReadOnly &&
    !props.options?.ignoreGlobalIsSummary
  ) {
    return <CheckboxSummaryControl {...props} />;
  }

  return (
    <Checkbox
      description={description}
      errors={errors}
      id={props.id}
      isCard={options.card}
      isDisabled={
        !props.enabled ||
        (isSummaryForPath?.(path) &&
          !props.uischema.options?.ignoreGlobalIsSummary)
      }
      isInvalid={props.invalid}
      isRequired={props.required}
      isSelected={props.data ?? false}
      name={props.path}
      onBlur={props.setTouched}
      style={options.textStyle || undefined}
      onChange={(value) => {
        props.setValue(value);
        props.setTouched();
      }}
      testID={`check-engine-${slugify(props.label)}-checkbox`}
      labelAlign={props.uischema.options?.labelAlign}
    >
      {props.label}
    </Checkbox>
  );
}

CheckboxControl.displayName = "CheckboxControl";

export const CheckboxRenderer = withCascadiaControlProps(CheckboxControl);
